// The fullscreen shell's transaction mirror, as a pure planner. The shell
// (ui-v2.js setTransaction) is a dumb display; the truth it must show is a
// composite: the placement machine for in-flight submissions, the coupons
// read model for the durable accepted ticket, and the tote draft for the
// player's intent to add more bets. Mirroring the placement machine alone
// is the bug this module exists to fix: its post-acceptance receipt reset
// (and its hand-over to the next betting window) look like "no bets" and
// used to wipe the accepted ticket off the in-game rail mid-window.
//
// planShellTicketPush is called from a React effect with the previous
// memory; it returns the setTransaction call to make (if any) plus the next
// memory. Pushes are keyed by a content signature so the shell is never
// re-rendered (or re-announced) for state it already shows.

import type { ToteLine } from "./hrwTote";
import type { PlacementState } from "./useV2Betting";

export type ShellTicketCouponsInput = {
  hasTicket: boolean;
  acceptedLines: ToteLine[];
  stakeCents: number;
  /** Durable ticket reference — the earliest coupon id for the race. */
  reference: string | null;
  /** Earliest coupon placedAt (ISO), server truth for "Accepted at". */
  placedAt: string | null;
  /** Every coupon id already reflected in acceptedLines (dedupe anchor). */
  couponIds: string[];
};

export type ShellTicketInputs = {
  /** The race the shell is presenting (the viewed racecard). */
  raceId: string;
  placement: PlacementState;
  coupons: ShellTicketCouponsInput;
  /** The viewed race's own betting window (market AND clock). */
  bettingOpen: boolean;
  /** Draft lines currently in the shared tote. */
  draftCount: number;
  formatAmount: (cents: number) => string;
};

export type ShellTicketCommand = {
  transaction:
    "draft" | "submitting" | "rejected" | "status-unknown" | "accepted";
  detail: Record<string, unknown>;
};

type AcceptedDisplay = {
  lines: ToteLine[];
  stake: number;
  reference: string;
  acceptedAt: string;
};

export type ShellTicketMemory = {
  /** Race the shell currently presents; a change resets everything. */
  raceId: string | null;
  /** Content signature of the last push — the idempotency key. */
  pushedSignature: string | null;
  /** Transaction kind the shell currently displays (announce policy). */
  pushedKind: string | null;
  /** Submission whose acceptance already animated (once per ticket). */
  animatedSubmissionId: string | null;
  /**
   * Last accepted display pushed for this race. Bridges the gap where the
   * placement machine has already reset to draft but the coupons query has
   * not confirmed yet — the shell must never claim "no bets" in between.
   */
  lastAccepted: AcceptedDisplay | null;
};

export function emptyShellTicketMemory(): ShellTicketMemory {
  return {
    raceId: null,
    pushedSignature: null,
    pushedKind: null,
    animatedSubmissionId: null,
    lastAccepted: null,
  };
}

function formatAcceptedAt(iso: string | null): string {
  if (!iso) return "";
  const stamp = new Date(iso);
  return Number.isNaN(stamp.getTime()) ? "" : stamp.toLocaleTimeString();
}

function coalesceLines(lines: ToteLine[]): ToteLine[] {
  const byKey = new Map<string, ToteLine>();
  for (const line of lines) {
    const existing = byKey.get(line.key);
    if (existing) {
      byKey.set(line.key, {
        ...existing,
        amount: existing.amount + line.amount,
      });
    } else {
      byKey.set(line.key, { ...line, sel: [...line.sel] });
    }
  }
  return [...byKey.values()];
}

/**
 * The accepted ticket the shell should display right now. Coupons are the
 * durable truth; a just-accepted placement whose coupons have not landed in
 * the read model yet (coupons lag placement by one refetch) is merged in by
 * coupon id so nothing double-counts and nothing disappears.
 */
function acceptedDisplay(
  inputs: ShellTicketInputs,
  memory: ShellTicketMemory,
): AcceptedDisplay | null {
  const { placement, coupons } = inputs;
  const placementAccepted =
    placement.transaction === "accepted" &&
    (!placement.raceId || placement.raceId === inputs.raceId)
      ? placement.accepted
      : [];
  const known = new Set(coupons.couponIds);
  const unmatched = placementAccepted.filter(
    (line) => !line.couponId || !known.has(line.couponId),
  );
  const lines = coalesceLines([...coupons.acceptedLines, ...unmatched]);
  if (lines.length) {
    const stake =
      coupons.stakeCents +
      unmatched.reduce((sum, line) => sum + line.amount, 0);
    return {
      lines,
      stake,
      reference: (coupons.reference ?? placement.submissionId ?? "").slice(
        0,
        8,
      ),
      acceptedAt:
        formatAcceptedAt(coupons.placedAt) ||
        placement.acceptedAt ||
        memory.lastAccepted?.acceptedAt ||
        "",
    };
  }
  // Neither source has lines. If this race already showed an accepted
  // ticket, keep showing it (a transient empty coupons response must not
  // wipe a confirmed ticket); otherwise there is genuinely no ticket.
  return memory.lastAccepted;
}

function acceptedSignature(
  display: AcceptedDisplay,
  keepBetting: boolean,
): string {
  const lines = display.lines
    .map((line) => `${line.key}=${line.amount}`)
    .sort()
    .join(",");
  return [
    "accepted",
    keepBetting ? "open" : "locked",
    display.stake,
    display.reference,
    display.acceptedAt,
    lines,
  ].join("|");
}

function acceptedMessage(
  display: AcceptedDisplay,
  formatAmount: (cents: number) => string,
): string {
  const count = display.lines.length;
  return `Accepted · ${count} bet${count === 1 ? "" : "s"} · ${formatAmount(display.stake)}`;
}

export function planShellTicketPush(
  previous: ShellTicketMemory,
  inputs: ShellTicketInputs,
): { command: ShellTicketCommand | null; memory: ShellTicketMemory } {
  // A new viewed race starts from a clean slate — the donor's
  // advance-to-next-card path. Without this, an accepted ticket from the
  // previous race would render against the new race's running order.
  const memory =
    previous.raceId === inputs.raceId
      ? previous
      : { ...emptyShellTicketMemory(), raceId: inputs.raceId };

  const { placement } = inputs;
  const placementForThisRace =
    !placement.raceId || placement.raceId === inputs.raceId;

  let command: ShellTicketCommand;
  let signature: string;
  let animatedSubmissionId = memory.animatedSubmissionId;
  let lastAccepted = memory.lastAccepted;

  if (placementForThisRace && placement.transaction === "submitting") {
    command = { transaction: "submitting", detail: {} };
    signature = `submitting|${placement.submissionId ?? ""}`;
  } else if (placementForThisRace && placement.transaction === "rejected") {
    command = {
      transaction: "rejected",
      detail: { message: placement.message },
    };
    signature = `rejected|${placement.revision}|${placement.message}`;
  } else if (
    placementForThisRace &&
    placement.transaction === "status-unknown"
  ) {
    command = {
      transaction: "status-unknown",
      detail: { message: placement.message },
    };
    signature = `status-unknown|${placement.revision}|${placement.message}`;
  } else {
    const freshAcceptance =
      placementForThisRace &&
      placement.transaction === "accepted" &&
      Boolean(placement.submissionId) &&
      placement.submissionId !== memory.animatedSubmissionId;
    const drafting = inputs.bettingOpen && inputs.draftCount > 0;
    const display = acceptedDisplay(inputs, memory);

    if (display && (freshAcceptance || !drafting)) {
      // The accepted ticket is the steady display for this race: through
      // the open window (keepBetting), the lock, the countdown and into
      // the race. Only an active new draft supersedes it visually.
      signature = acceptedSignature(display, inputs.bettingOpen);
      const kindChanged = memory.pushedKind !== "accepted";
      const lockFlip =
        !kindChanged &&
        !inputs.bettingOpen &&
        memory.pushedSignature?.startsWith("accepted|open|") === true;
      const message =
        freshAcceptance && placement.message
          ? placement.message
          : acceptedMessage(display, inputs.formatAmount);
      // Announce real transitions; content refinements (e.g. the reference
      // upgrading from submission id to coupon id) stay silent.
      const announcement =
        freshAcceptance || kindChanged
          ? undefined
          : lockFlip
            ? `Ticket locked in. ${message}.`
            : null;
      command = {
        transaction: "accepted",
        detail: {
          lines: display.lines,
          stake: display.stake,
          raceId: inputs.raceId,
          reference: display.reference,
          acceptedAt: display.acceptedAt,
          animateAcceptance: freshAcceptance,
          keepBetting: inputs.bettingOpen,
          message,
          ...(announcement !== undefined ? { announcement } : {}),
        },
      };
      if (freshAcceptance) animatedSubmissionId = placement.submissionId;
      lastAccepted = display;
    } else {
      command = { transaction: "draft", detail: {} };
      signature = "draft";
    }
  }

  if (memory === previous && previous.pushedSignature === signature) {
    // Same content already on screen — no push, but keep the freshest
    // accepted display and animation bookkeeping.
    return {
      command: null,
      memory:
        animatedSubmissionId === previous.animatedSubmissionId &&
        lastAccepted === previous.lastAccepted
          ? previous
          : { ...previous, animatedSubmissionId, lastAccepted },
    };
  }
  return {
    command,
    memory: {
      raceId: inputs.raceId,
      pushedSignature: signature,
      pushedKind: command.transaction,
      animatedSubmissionId,
      lastAccepted,
    },
  };
}
